import { slugJavaslat } from '@ertekbecslo/kozos/slug';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { edgeHivas } from '../lib/edge.ts';
import { formatDatumIdo, telefonMegjelenites } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { irodaUrlapHibak, irodaUrlapUres } from '../super/irodaUrlap.ts';
import {
  ALLAPOT_CIMKE, allapotE, figyelmeztetesek, halozatNev, meghivasSzoveg, meghivasUjrakuldheto, REGISZTRACIO_OSZLOPOK,
  type RegisztracioAllapot, type RegSor,
} from '../super/regisztracioLista.ts';
import { Betolto, Gomb, HibaUzenet, Kartya, Mezo, OldalFej, Parbeszed } from '../ui/elemek.tsx';

interface DontesValasz { ok: true; officeId?: string; meghivas?: string; meghivasHiba?: string }

function Sor({ cimke, ertek }: { cimke: string; ertek: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-0.5 py-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-3">
      <dt className="text-sm text-szoveg-halk">{cimke}</dt>
      <dd className="wrap-anywhere font-medium">{ertek || '–'}</dd>
    </div>
  );
}

function Blokk({ cim, children }: { cim: string; children: ReactNode }) {
  return (
    <Kartya>
      <h2 className="mb-1 text-[17px] font-medium tracking-[-0.015em]">{cim}</h2>
      <dl className="divide-y divide-vonal">{children}</dl>
    </Kartya>
  );
}

const igenNem = (b: boolean) => (b ? 'Igen' : 'Nem');

function Figyelmeztetesek({ lista }: { lista: string[] }) {
  if (lista.length === 0) return null;
  return (
    <ul className="grid gap-1.5 rounded-xl border border-vonal-eros bg-felulet px-4 py-3 text-sm" aria-label="Figyelmeztetések">
      {lista.map((f) => <li key={f}><span aria-hidden>⚠ </span>{f}</li>)}
    </ul>
  );
}

/** A döntés (jóváhagyás/elutasítás) űrlapja; csak függő regisztrációnál jelenik meg, az adat betöltése után. */
function DontesBlokk({ sor, figyelmeztetesLista }: { sor: RegSor; figyelmeztetesLista: (slugFoglalt: boolean) => string[] }) {
  const kliens = useQueryClient();
  const [irodaNev, setIrodaNev] = useState(sor.iroda_nev);
  const [slug, setSlug] = useState(() => slugJavaslat(sor.iroda_nev));
  const [indok, setIndok] = useState('');
  const [probalt, setProbalt] = useState(false);
  const [jovahagyDialog, setJovahagyDialog] = useState(false);
  const [elutasitDialog, setElutasitDialog] = useState(false);

  const urlapHibak = irodaUrlapHibak({ ...irodaUrlapUres(), slug, nev: irodaNev });
  const slugHiba = urlapHibak.slug;
  const nevHiba = irodaNev.trim().length < 2 ? 'Legalább 2 karakter.' : irodaNev.trim().length > 120 ? 'Legfeljebb 120 karakter.' : undefined;
  const indokHiba = indok.trim() === '' ? 'Az indok megadása kötelező.' : indok.trim().length > 1000 ? 'Legfeljebb 1000 karakter.' : undefined;

  // A foglalt slugot kliensoldalon is jelezzük; a döntő ellenőrzés az adatbázisé (409 slug_foglalt).
  const foglalt = useQuery({
    queryKey: ['super-regisztracio-slug', slug.trim()],
    enabled: !slugHiba,
    queryFn: async () => {
      const { count, error } = await supabase.from('offices').select('id', { count: 'exact', head: true }).eq('slug', slug.trim());
      if (error) throw error;
      return (count ?? 0) > 0;
    },
  });

  const dontes = useMutation({
    mutationFn: (torzs: Record<string, string>) => edgeHivas<DontesValasz>('iroda-regisztracio-dontes', { id: sor.id, ...torzs }),
    onSettled: () => {
      void kliens.invalidateQueries({ queryKey: ['super-regisztraciok'] });
      void kliens.invalidateQueries({ queryKey: ['super-regisztracio', sor.id] });
      void kliens.invalidateQueries({ queryKey: ['super-irodak'] });
    },
  });

  const jovahagyKer = () => {
    setProbalt(true);
    if (slugHiba || nevHiba || foglalt.data) return;
    setJovahagyDialog(true);
  };
  const elutasitKer = () => {
    setProbalt(true);
    if (indokHiba) return;
    setElutasitDialog(true);
  };

  return (
    <Kartya className="grid gap-5">
      <h2 className="text-[17px] font-medium tracking-[-0.015em]">Döntés</h2>
      <Figyelmeztetesek lista={figyelmeztetesLista(foglalt.data === true)} />
      <div className="grid gap-4">
        <h3 className="font-medium">Jóváhagyás</h3>
        <p className="text-sm text-szoveg-halk">
          Az iroda inaktívan jön létre, nyilvános oldal nélkül. A kapcsolattartó irodavezetői meghívót kap. Az aktiválás az Egyedi Szerződés aláírása után, az iroda szerkesztőjében történik.
        </p>
        <Mezo label="Az iroda neve" value={irodaNev} onChange={(e) => setIrodaNev(e.target.value)} hiba={probalt ? nevHiba : undefined} />
        <Mezo label="Slug (az oldal címe)" value={slug} onChange={(e) => setSlug(e.target.value)} autoCapitalize="none"
          hiba={probalt || slug !== '' ? slugHiba : undefined} suly="Pl. duna-house-xiii; a javaslat a névből készült." />
        <div><Gomb onClick={jovahagyKer} disabled={dontes.isPending}>Jóváhagyás…</Gomb></div>
      </div>
      <div className="grid gap-4 border-t border-vonal pt-5">
        <h3 className="font-medium">Elutasítás</h3>
        <Mezo label="Az elutasítás indoka (a kapcsolattartó megkapja)" multiline value={indok} maxLength={1000}
          onChange={(e) => setIndok(e.target.value)} hiba={probalt ? indokHiba : undefined} />
        <div><Gomb valtozat="veszely" onClick={elutasitKer} disabled={dontes.isPending}>Elutasítás…</Gomb></div>
      </div>
      <HibaUzenet hiba={dontes.error} />

      <Parbeszed nyitva={jovahagyDialog} cim="Biztosan jóváhagyod?" onBezar={() => setJovahagyDialog(false)}>
        <p>Létrehozzuk az irodát (nyilvános oldal nélkül), és meghívjuk {sor.kapcsolattarto_nev} irodavezetőnek.</p>
        <div className="flex flex-wrap justify-end gap-3">
          <Gomb valtozat="masodlagos" onClick={() => setJovahagyDialog(false)}>Mégsem</Gomb>
          <Gomb toltes={dontes.isPending} onClick={() => { setJovahagyDialog(false); dontes.mutate({ dontes: 'jovahagyas', irodaNev: irodaNev.trim(), slug: slug.trim() }); }}>
            Jóváhagyás és meghívás
          </Gomb>
        </div>
      </Parbeszed>
      <Parbeszed nyitva={elutasitDialog} cim="Biztosan elutasítod?" onBezar={() => setElutasitDialog(false)}>
        <p>A kapcsolattartó e-mailben megkapja az indokot, fiók nem keletkezik. A döntés nem vonható vissza.</p>
        <div className="flex flex-wrap justify-end gap-3">
          <Gomb valtozat="masodlagos" onClick={() => setElutasitDialog(false)}>Mégsem</Gomb>
          <Gomb valtozat="veszely" toltes={dontes.isPending} onClick={() => { setElutasitDialog(false); dontes.mutate({ dontes: 'elutasitas', indok: indok.trim() }); }}>
            Elutasítás
          </Gomb>
        </div>
      </Parbeszed>
    </Kartya>
  );
}

/** A jóváhagyott regisztráció eredménye: a meghívás állapota, újraküldés, az új iroda szerkesztője. */
function EredmenyBlokk({ sor }: { sor: RegSor }) {
  const kliens = useQueryClient();
  const iroda = useQuery({
    queryKey: ['super-regisztracio-iroda', sor.office_id],
    enabled: sor.office_id !== null,
    queryFn: async () => {
      const { data, error } = await supabase.from('offices').select('id, nev, slug, aktiv').eq('id', sor.office_id!).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  // Ugyanaz a döntéshívás ismételhető: létező irodánál csak a meghívást ismétli (új iroda nem keletkezik).
  const ujra = useMutation({
    mutationFn: () => edgeHivas<DontesValasz>('iroda-regisztracio-dontes', { id: sor.id, dontes: 'jovahagyas', irodaNev: iroda.data!.nev, slug: iroda.data!.slug }),
    onSettled: () => void kliens.invalidateQueries({ queryKey: ['super-regisztracio', sor.id] }),
  });

  return (
    <Kartya className="grid gap-3">
      <h2 className="text-[17px] font-medium tracking-[-0.015em]">Jóváhagyva</h2>
      <p role="status">{meghivasSzoveg(sor.meghivas_allapot, sor.meghivas_hiba)}</p>
      {meghivasUjrakuldheto(sor.meghivas_allapot) && (
        <div>
          <Gomb valtozat="masodlagos" toltes={ujra.isPending} disabled={!iroda.data} onClick={() => ujra.mutate()}>Meghívás újraküldése</Gomb>
        </div>
      )}
      <HibaUzenet hiba={ujra.error ?? iroda.error} />
      {sor.office_id && (
        <p className="text-sm text-szoveg-halk">
          Az iroda inaktív, nyilvános oldal nélkül. <b>Nyilvános oldal aktiválása az Egyedi Szerződés után</b>:{' '}
          <Link to={`/super/irodak/${sor.office_id}`} className="text-kiemelt underline">az iroda szerkesztője{iroda.data ? ` (${iroda.data.nev})` : ''}</Link>.
        </p>
      )}
    </Kartya>
  );
}

export default function SuperRegisztracio() {
  const { id } = useParams<{ id: string }>();

  const betoltes = useQuery({
    queryKey: ['super-regisztracio', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('iroda_regisztraciok').select(REGISZTRACIO_OSZLOPOK).eq('id', id!).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const r = betoltes.data;

  const azonosAdoszam = useQuery({
    queryKey: ['super-regisztracio-adoszam', r?.adoszam],
    enabled: !!r,
    queryFn: async () => {
      const { data, error } = await supabase.from('iroda_regisztraciok').select('id, adoszam, allapot').eq('adoszam', r!.adoszam);
      if (error) throw error;
      return data.filter((x): x is typeof x & { allapot: RegisztracioAllapot } => allapotE(x.allapot));
    },
  });
  const profil = useQuery({
    queryKey: ['super-regisztracio-profil', r?.kapcsolattarto_email],
    enabled: !!r,
    queryFn: async () => {
      const { count, error } = await supabase.from('profiles').select('user_id', { count: 'exact', head: true }).eq('email', r!.kapcsolattarto_email);
      if (error) throw error;
      return (count ?? 0) > 0;
    },
  });

  if (betoltes.isPending) return <Betolto />;
  if (betoltes.error) return <HibaUzenet hiba={betoltes.error} />;
  if (!r) return <p>Nincs ilyen regisztráció.</p>;

  const allapot = allapotE(r.allapot) ? r.allapot : 'megerosites_var';
  const figyelmeztetesLista = (slugFoglalt: boolean) => figyelmeztetesek(r, azonosAdoszam.data ?? [], profil.data === true, slugFoglalt);

  return (
    <div className="grid max-w-3xl gap-4">
      <OldalFej cim={r.iroda_nev} leiras={`${ALLAPOT_CIMKE[allapot]} · beküldve: ${formatDatumIdo(r.created_at)}`}>
        <Link to="/super/regisztraciok" className="text-sm text-kiemelt underline">Vissza a listához</Link>
      </OldalFej>

      {allapot === 'fuggo' && <DontesBlokk sor={r} figyelmeztetesLista={figyelmeztetesLista} />}
      {allapot === 'megerosites_var' && (
        <Kartya>
          <p>A kapcsolattartó még nem erősítette meg az e-mail-címét, ezért a regisztráció még nem bírálható el.
            {r.megerosites_lejar ? ` A megerősítő link érvényes: ${formatDatumIdo(r.megerosites_lejar)}-ig.` : ''}</p>
        </Kartya>
      )}
      {allapot === 'jovahagyva' && <EredmenyBlokk sor={r} />}
      {allapot === 'elutasitva' && (
        <Kartya className="grid gap-2">
          <h2 className="text-[17px] font-medium tracking-[-0.015em]">Elutasítva</h2>
          <p className="text-sm text-szoveg-halk">{r.dontes_at ? formatDatumIdo(r.dontes_at) : ''}</p>
          <p className="whitespace-pre-wrap">{r.elutasitas_indok}</p>
        </Kartya>
      )}
      {allapot !== 'fuggo' && <Figyelmeztetesek lista={figyelmeztetesLista(false)} />}

      <Blokk cim="Iroda">
        <Sor cimke="Megjelenő név" ertek={r.iroda_nev} />
        <Sor cimke="Hálózat" ertek={halozatNev(r)} />
        <Sor cimke="Település" ertek={r.iroda_telepules} />
        <Sor cimke="Régió" ertek={r.iroda_regio} />
        <Sor cimke="Utca, házszám" ertek={r.iroda_cim} />
      </Blokk>
      <Blokk cim="Cég">
        <Sor cimke="Cégforma" ertek={r.ceg_forma === 'tarsasag' ? 'Társaság' : 'Egyéni vállalkozó'} />
        <Sor cimke="Cégnév" ertek={r.cegnev} />
        <Sor cimke="Székhely" ertek={r.szekhely} />
        <Sor cimke="Adószám" ertek={r.adoszam} />
        {r.ceg_forma === 'tarsasag'
          ? <Sor cimke="Cégjegyzékszám" ertek={r.cegjegyzekszam} />
          : <Sor cimke="Nyilvántartási szám" ertek={r.ev_nyilvantartasi_szam} />}
        <Sor cimke="Számlázási e-mail" ertek={r.szamlazasi_email} />
      </Blokk>
      <Blokk cim="Kapcsolattartó">
        <Sor cimke="Név" ertek={r.kapcsolattarto_nev} />
        <Sor cimke="E-mail" ertek={r.kapcsolattarto_email} />
        <Sor cimke="Mobilszám" ertek={telefonMegjelenites(r.kapcsolattarto_telefon)} />
        <Sor cimke="Megjegyzés" ertek={r.megjegyzes && <span className="whitespace-pre-wrap">{r.megjegyzes}</span>} />
      </Blokk>
      <Blokk cim="Elfogadás és nyilatkozatok">
        <Sor cimke="Partner ÁSZF" ertek={`${r.aszf_verzio} verzió (azonosító: ${r.aszf_hash})`} />
        <Sor cimke="Adatvédelmi tájékoztató" ertek={`${r.adatvedelem_verzio} verzió (azonosító: ${r.adatvedelem_hash})`} />
        <Sor cimke="Elfogadás ideje" ertek={formatDatumIdo(r.elfogadva_at)} />
        <Sor cimke="Képviseleti nyilatkozat" ertek={igenNem(r.kepviseleti_nyilatkozat)} />
        <Sor cimke="Hálózati nyilatkozat" ertek={r.halozat_kulcs ? igenNem(r.halozati_nyilatkozat) : 'Nem szükséges'} />
        <Sor cimke="E-mail megerősítve" ertek={r.email_megerositve_at ? formatDatumIdo(r.email_megerositve_at) : 'Még nem'} />
        <Sor cimke="Böngésző" ertek={r.user_agent} />
      </Blokk>
    </div>
  );
}
